/**
 * Collision geometry is streamed in square columns of `size` metres on the ground plane (render
 * space: x and z, y up). A triangle belongs to every column its bounding box overlaps, so a column's
 * collider is complete on its own. Pure (no three.js or Rapier) so it can be unit-tested.
 */

export const cellKey = (cx: number, cz: number): string => `${cx},${cz}`;

/**
 * Group the triangles of an indexed mesh (world-space positions) by column. Returns column key →
 * triangle indices (into `indices / 3`).
 */
export function bucketTriangles(positions: Float32Array, indices: ArrayLike<number>, size: number): Map<string, Uint32Array> {
  const lists = new Map<string, number[]>();
  const n = Math.floor(indices.length / 3);
  for (let t = 0; t < n; t++) {
    let x0 = Infinity;
    let x1 = -Infinity;
    let z0 = Infinity;
    let z1 = -Infinity;
    for (let k = 0; k < 3; k++) {
      const i = indices[t * 3 + k] * 3;
      const x = positions[i];
      const z = positions[i + 2];
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (z < z0) z0 = z;
      if (z > z1) z1 = z;
    }
    if (!Number.isFinite(x0 + x1 + z0 + z1)) continue;
    const ca = Math.floor(x0 / size);
    const cb = Math.floor(x1 / size);
    const cc = Math.floor(z0 / size);
    const cd = Math.floor(z1 / size);
    for (let cx = ca; cx <= cb; cx++)
      for (let cz = cc; cz <= cd; cz++) {
        const key = cellKey(cx, cz);
        let l = lists.get(key);
        if (!l) lists.set(key, (l = []));
        l.push(t);
      }
  }
  const out = new Map<string, Uint32Array>();
  for (const [k, l] of lists) out.set(k, Uint32Array.from(l));
  return out;
}

/** The vertices and re-numbered indices of a subset of a mesh's triangles (for one trimesh collider). */
export function compactTriangles(positions: Float32Array, indices: ArrayLike<number>, tris: Uint32Array): { vertices: Float32Array; indices: Uint32Array } {
  const remap = new Map<number, number>();
  const out = new Uint32Array(tris.length * 3);
  const verts: number[] = [];
  let w = 0;
  for (let j = 0; j < tris.length; j++) {
    const t = tris[j];
    const a = indices[t * 3];
    const b = indices[t * 3 + 1];
    const c = indices[t * 3 + 2];
    // Degenerate triangles (repeated index) give nothing to collide with.
    if (a === b || b === c || a === c) continue;
    for (const v of [a, b, c]) {
      let r = remap.get(v);
      if (r === undefined) {
        r = verts.length / 3;
        remap.set(v, r);
        verts.push(positions[v * 3], positions[v * 3 + 1], positions[v * 3 + 2]);
      }
      out[w++] = r;
    }
  }
  return { vertices: Float32Array.from(verts), indices: out.slice(0, w) };
}

/** Column keys within `radius` of (x, z) (columns whose square touches the disc). */
export function cellsAround(x: number, z: number, radius: number, size: number): string[] {
  return cellsAroundDist(x, z, radius, size).map((c) => c.key);
}

/** The columns within `radius` of (x, z), nearest first, with their distance (m) from the point. */
export function cellsAroundDist(x: number, z: number, radius: number, size: number): { key: string; d: number }[] {
  const keys: { key: string; d: number }[] = [];
  const ca = Math.floor((x - radius) / size);
  const cb = Math.floor((x + radius) / size);
  const cc = Math.floor((z - radius) / size);
  const cd = Math.floor((z + radius) / size);
  for (let cx = ca; cx <= cb; cx++)
    for (let cz = cc; cz <= cd; cz++) {
      const dx = Math.max(cx * size - x, 0, x - (cx + 1) * size);
      const dz = Math.max(cz * size - z, 0, z - (cz + 1) * size);
      if (dx * dx + dz * dz <= radius * radius) keys.push({ key: cellKey(cx, cz), d: Math.hypot(dx, dz) });
    }
  return keys.sort((a, b) => a.d - b.d);
}
